import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import { IconX } from '@tabler/icons-react';
import { TableType } from '@common/ts/enums';
import { getPaginationParamPage } from '@/utils/urlParams';
import { getTranscriptFilterParamName } from '@/hooks/useTranscriptsFilters';

const DEBOUNCE_MS = 400;
// Placeholder width when the pill is empty — wide enough to show the
// full "Search…" hint so the user knows to type. Once typing starts,
// the input shrinks/grows with the value via the `size` attribute.
const PLACEHOLDER_TEXT = 'Search…';
const MIN_INPUT_SIZE = PLACEHOLDER_TEXT.length;

interface FilterPillProps {
  columnId: string;
  label: string;
  autoFocus?: boolean;
  onRemove: (columnId: string) => void;
}

/**
 * A single filter expressed as an editable pill. URL is the source of
 * truth for the value; local state buffers keystrokes and writes
 * debounced. Focus survives refetches because the pill lives outside
 * the column header and the table stays mounted via placeholderData.
 *
 * Autofocus intentionally uses a ref + setTimeout(0) rather than the
 * autoFocus attribute — the attribute races with Radix Popover close
 * animations when the picker adds a new pill.
 */
export function FilterPill({ columnId, label, autoFocus, onRemove }: FilterPillProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramName = getTranscriptFilterParamName(columnId);
  const urlValue = searchParams.get(paramName) ?? '';

  const [local, setLocal] = useState(urlValue);
  const [debounced] = useDebounce(local, DEBOUNCE_MS);
  const inputRef = useRef<HTMLInputElement>(null);

  // URL → local: keeps input in sync with back/forward, clear-all, bookmark.
  useEffect(() => {
    setLocal(urlValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlValue]);

  // Local (debounced) → URL. Functional updater avoids stale-state overwrite.
  useEffect(() => {
    if (debounced === urlValue) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const trimmed = debounced.trim();
        if (trimmed) next.set(paramName, trimmed);
        else next.delete(paramName);
        // Filter change invalidates page cursors across all transcript views.
        next.delete(getPaginationParamPage(TableType.TRANSCRIPTS));
        next.delete(getPaginationParamPage(TableType.INCOMPLETE_TRANSCRIPTS));
        next.delete(getPaginationParamPage(TableType.SCREENED_OUT_TRANSCRIPTS));
        next.delete(getPaginationParamPage(TableType.TRANSLATED_TRANSCRIPTS));
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  // Focus the input synchronously during commit — runs before Radix
  // Popover's trigger-refocus on close, so focus lands in the pill
  // instead of bouncing back to the picker button.
  useLayoutEffect(() => {
    if (!autoFocus) return;
    inputRef.current?.focus();
  }, [autoFocus]);

  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-natural-700 bg-gray-50 h-7 pl-2.5 pr-1.5 text-xs text-gray-700 max-w-[18rem]">
      <span
        className="font-medium text-gray-700 shrink-0 max-w-[10rem] truncate"
        title={label}
      >
        {label}:
      </span>
      <input
        ref={inputRef}
        type="text"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        size={Math.max(local.length, MIN_INPUT_SIZE)}
        placeholder={PLACEHOLDER_TEXT}
        className="bg-transparent border-none outline-none p-0 text-xs text-gray-600 placeholder:text-gray-400 w-auto"
      />
      <button
        type="button"
        onClick={() => onRemove(columnId)}
        className="ml-0.5 text-gray-400 hover:text-gray-700 rounded-full p-0.5"
        title="Remove filter"
      >
        <IconX size={10} />
      </button>
    </div>
  );
}
